/**
 * BudgetEditor — Create/edit budget with 12-month line items and auto-calculated totals.
 *
 * Requirements: 45.1, 45.2, 45.3, 45.14, 45.15, 45.16
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { budgetService, CreateLineItemInput } from '@/services/budgetService';
import type { Budget, BudgetLineItem } from '@/types';

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_KEYS = [
  'month01', 'month02', 'month03', 'month04', 'month05', 'month06',
  'month07', 'month08', 'month09', 'month10', 'month11', 'month12',
] as const;

function calculateTotal(item: Partial<CreateLineItemInput>): number {
  return MONTH_KEYS.reduce((sum, key) => sum + ((item[key] as number) ?? 0), 0);
}

export function BudgetEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';

  const [budget, setBudget] = useState<Budget | null>(null);
  const [lineItems, setLineItems] = useState<BudgetLineItem[]>([]);
  const [fiscalYear, setFiscalYear] = useState(new Date().getFullYear() + 1);
  const [budgetName, setBudgetName] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadBudget = useCallback(async () => {
    if (isNew) return;
    try {
      setLoading(true);
      const detail = await budgetService.getById(Number(id));
      setBudget(detail.budget);
      setLineItems(detail.lineItems);
      setFiscalYear(detail.budget.fiscalYear);
      setBudgetName(detail.budget.budgetName ?? '');
      setNotes(detail.budget.notes ?? '');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load budget.');
    } finally {
      setLoading(false);
    }
  }, [id, isNew]);

  useEffect(() => { loadBudget(); }, [loadBudget]);

  const handleSaveBudget = async () => {
    setSaving(true);
    setError(null);
    try {
      if (isNew) {
        const created = await budgetService.create({ fiscalYear, budgetName, notes });
        navigate(`/budgets/${created.budgetId}`, { replace: true });
      } else if (budget) {
        await budgetService.update(Number(id), {
          budgetName, notes, updatedAt: budget.updatedAt,
        });
        await loadBudget();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save budget.');
    } finally {
      setSaving(false);
    }
  };

  const handleAddLineItem = async () => {
    if (!budget) return;
    try {
      const newItem = await budgetService.createLineItem(budget.budgetId, {
        description: 'New line item',
      });
      setLineItems(prev => [...prev, newItem]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add line item.');
    }
  };

  const handleDeleteLineItem = async (lineItemId: number) => {
    try {
      await budgetService.deleteLineItem(lineItemId);
      setLineItems(prev => prev.filter(li => li.lineItemId !== lineItemId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete line item.');
    }
  };

  const handleExport = () => {
    if (!budget) return;
    window.open(budgetService.getExportUrl(budget.budgetId), '_blank');
  };

  if (loading) return <div >Loading budget...</div>;

  return (
    <div className="p-6 max-w-full">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-lg font-semibold text-text-primary">
          {isNew ? 'Create Budget' : `Edit Budget — FY${fiscalYear}`}
        </h1>
        <div className="flex gap-2">
          {budget && (
            <button onClick={handleExport} className="px-4 py-2 border rounded hover:bg-page">
              Export CSV
            </button>
          )}
          <button
            onClick={handleSaveBudget}
            disabled={saving}
            className="px-4 py-2 bg-baker-red text-white rounded-lg font-medium hover:bg-baker-red/90 hover:bg-baker-red/90 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Budget'}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded" role="alert">
          {error}
        </div>
      )}

      <div className="grid grid-cols-3 gap-4 mb-6">
        <div>
          <label htmlFor="fiscalYear" className="block text-sm font-medium mb-1">Fiscal Year</label>
          <input
            id="fiscalYear"
            type="number"
            value={fiscalYear}
            onChange={e => setFiscalYear(Number(e.target.value))}
            disabled={!isNew}
            className="w-full py-2 px-3 rounded-lg border border-border text-sm focus:outline-none focus:ring-2 focus:ring-baker-accent/40"
            min={2000} max={2100}
          />
        </div>
        <div>
          <label htmlFor="budgetName" className="block text-sm font-medium mb-1">Budget Name</label>
          <input
            id="budgetName"
            type="text"
            value={budgetName}
            onChange={e => setBudgetName(e.target.value)}
            className="w-full py-2 px-3 rounded-lg border border-border text-sm focus:outline-none focus:ring-2 focus:ring-baker-accent/40"
            placeholder="e.g., IT Operations Budget"
          />
        </div>
        <div>
          <label htmlFor="notes" className="block text-sm font-medium mb-1">Notes</label>
          <input
            id="notes"
            type="text"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            className="w-full py-2 px-3 rounded-lg border border-border text-sm focus:outline-none focus:ring-2 focus:ring-baker-accent/40"
          />
        </div>
      </div>

      {budget && (
        <>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium">Line Items</h2>
            <button
              onClick={handleAddLineItem}
              className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700"
            >
              + Add Line Item
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-page">
                  <th className="border px-2 py-1 text-left">GL Code</th>
                  <th className="border px-2 py-1 text-left">Description</th>
                  <th className="border px-2 py-1 text-left">Category</th>
                  {MONTH_LABELS.map(m => (
                    <th key={m} className="border px-2 py-1 text-right">{m}</th>
                  ))}
                  <th className="border px-2 py-1 text-right font-bold">Annual</th>
                  <th className="border px-2 py-1">Actions</th>
                </tr>
              </thead>
              <tbody>
                {lineItems.map(li => (
                  <LineItemRow
                    key={li.lineItemId}
                    item={li}
                    onUpdate={updated => {
                      setLineItems(prev => prev.map(x => x.lineItemId === updated.lineItemId ? updated : x));
                    }}
                    onDelete={() => handleDeleteLineItem(li.lineItemId)}
                  />
                ))}
                {lineItems.length === 0 && (
                  <tr>
                    <td colSpan={16} className="border px-4 py-8 text-center text-slate-500">
                      No line items yet. Click "Add Line Item" to get started.
                    </td>
                  </tr>
                )}
              </tbody>
              {lineItems.length > 0 && (
                <tfoot>
                  <tr className="bg-page font-bold">
                    <td colSpan={3} className="border px-2 py-1">Totals</td>
                    {MONTH_KEYS.map(key => (
                      <td key={key} className="border px-2 py-1 text-right">
                        {lineItems.reduce((sum, li) => sum + (li[key] ?? 0), 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                      </td>
                    ))}
                    <td className="border px-2 py-1 text-right">
                      {lineItems.reduce((sum, li) => sum + li.annualTotal, 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                    </td>
                    <td className="border px-2 py-1" />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </>
      )}
    </div>
  );
}

/** Inline-editable line item row. */
function LineItemRow({
  item,
  onUpdate,
  onDelete,
}: {
  item: BudgetLineItem;
  onUpdate: (updated: BudgetLineItem) => void;
  onDelete: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<CreateLineItemInput>({});
  const [saving, setSaving] = useState(false);

  const startEdit = () => {
    setDraft({
      glCode: item.glCode,
      description: item.description,
      budgetCategory: item.budgetCategory,
      month01: item.month01, month02: item.month02, month03: item.month03,
      month04: item.month04, month05: item.month05, month06: item.month06,
      month07: item.month07, month08: item.month08, month09: item.month09,
      month10: item.month10, month11: item.month11, month12: item.month12,
      notes: item.notes,
    });
    setEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await budgetService.updateLineItem(item.lineItemId, draft);
      onUpdate(updated);
      setEditing(false);
    } catch {
      // Error handled by parent
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <tr className="hover:bg-page">
        <td className="border px-2 py-1">{item.glCode ?? '—'}</td>
        <td className="border px-2 py-1">{item.description ?? '—'}</td>
        <td className="border px-2 py-1">{item.budgetCategory ?? '—'}</td>
        {MONTH_KEYS.map(key => (
          <td key={key} className="border px-2 py-1 text-right">
            {(item[key] ?? 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
          </td>
        ))}
        <td className="border px-2 py-1 text-right font-semibold">
          {item.annualTotal.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
        </td>
        <td className="border px-2 py-1">
          <div className="flex gap-1">
            <button onClick={startEdit} className="text-baker-red hover:underline text-xs">Edit</button>
            <button onClick={onDelete} className="text-red-600 hover:underline text-xs">Del</button>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr className="bg-baker-accent/10">
      <td className="border px-1 py-1">
        <input
          type="text"
          value={draft.glCode ?? ''}
          onChange={e => setDraft(d => ({ ...d, glCode: e.target.value || null }))}
          className="w-full border rounded px-1 py-0.5 text-sm"
          aria-label="GL Code"
        />
      </td>
      <td className="border px-1 py-1">
        <input
          type="text"
          value={draft.description ?? ''}
          onChange={e => setDraft(d => ({ ...d, description: e.target.value || null }))}
          className="w-full border rounded px-1 py-0.5 text-sm"
          aria-label="Description"
        />
      </td>
      <td className="border px-1 py-1">
        <input
          type="text"
          value={draft.budgetCategory ?? ''}
          onChange={e => setDraft(d => ({ ...d, budgetCategory: e.target.value || null }))}
          className="w-full border rounded px-1 py-0.5 text-sm"
          aria-label="Budget Category"
        />
      </td>
      {MONTH_KEYS.map(key => (
        <td key={key} className="border px-1 py-1">
          <input
            type="number"
            value={draft[key] ?? 0}
            onChange={e => setDraft(d => ({ ...d, [key]: Number(e.target.value) || 0 }))}
            className="w-20 border rounded px-1 py-0.5 text-sm text-right"
            aria-label={MONTH_LABELS[MONTH_KEYS.indexOf(key)]}
            step="0.01"
          />
        </td>
      ))}
      <td className="border px-2 py-1 text-right font-semibold">
        {calculateTotal(draft).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
      </td>
      <td className="border px-2 py-1">
        <div className="flex gap-1">
          <button
            onClick={handleSave}
            disabled={saving}
            className="text-green-600 hover:underline text-xs"
          >
            {saving ? '...' : 'Save'}
          </button>
          <button onClick={() => setEditing(false)} className="text-slate-600 hover:underline text-xs">
            Cancel
          </button>
        </div>
      </td>
    </tr>
  );
}
